/**
 * #369 — ¿`ai_raw_text` es de verdad lo que la persona escribió, o el modelo lo
 * reescribió en el camino?
 *
 * GEMELO EXACTO de `mcp-server/src/utils/rawTextFidelity.js`: el cuerpo de los dos
 * archivos es idéntico byte a byte y hay un test de paridad que lo verifica
 * (`mcp-server/tests/tools/rawTextFidelityLaneParity.test.js`). Se duplica a mano,
 * igual que `materialSplit.js` (#384), porque el bundle de CRA no resuelve imports
 * fuera de `src/` y el MCP server es un paquete aparte. Si cambiás uno, cambiá el
 * otro: los DOS carriles escriben `ai_raw_text` y `source_input_text`, así que la
 * regla que decide qué se puede mostrar entre comillas tiene que ser una sola.
 *
 * En ESTE carril el original es lo más cerca de la fuente que el sistema llega:
 * `SmartMaterialRequest.js` manda el textarea que el técnico tecleó, crudo.
 *
 * LA MEDICIÓN QUE ORIGINÓ ESTE ARCHIVO (prod, 27-jul-2026, 149 ítems): de los 78
 * ítems donde se puede comparar, 16 (20,5%) NO son cita textual. En dos corridas
 * distintas el modelo guardó "2 codos de aluminio de 90°, de 4 pulgadas" cuando
 * el original decía "2 codos de aluminio de 90° de 4 pulgadas".
 */

/**
 * Normaliza para la comparación "tolerante": minúsculas, sin acentos, sin
 * puntuación y con espacios colapsados. Es a propósito MÁS permisiva que el
 * criterio de cita: si algo pasa acá pero no en la comparación estricta, el
 * modelo editó el texto — poco, pero editó.
 */
export const normalizeForComparison = (text) => {
  if (text == null) return '';
  return String(text)
    .toLowerCase()
    .normalize('NFD')
    // \p{M} = marcas combinantes. Se usa la propiedad Unicode en vez de escribir
    // el rango con caracteres literales: un rango literal se corrompe si el
    // archivo se re-codifica y el bug quedar\u00eda invisible (la regex seguir\u00eda
    // compilando, s\u00f3lo dejar\u00eda de sacar acentos).
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/** Estados posibles. Ver el comentario de cabecera para qué prueba cada uno. */
export const RAW_TEXT_STATUS = {
  /** Aparece IGUAL, carácter por carácter, dentro del original guardado. */
  VERBATIM: 'verbatim',
  /** Sólo coincide tras normalizar → el modelo lo retocó. No es cita. */
  PARAPHRASED: 'paraphrased',
  /** No aparece en el original de ninguna forma. No es cita. */
  DIVERGENT: 'divergent',
  /** No hay original guardado: no hay contra qué comparar. No es cita. */
  NO_ORIGINAL: 'no_original',
};

/**
 * @param {string|null|undefined} rawText           material_request_items.ai_raw_text
 * @param {string|null|undefined} sourceInputText   material_requests.source_input_text
 * @returns {string|null} uno de RAW_TEXT_STATUS, o null si no hay rawText que juzgar.
 */
export const classifyRawText = (rawText, sourceInputText) => {
  const raw = typeof rawText === 'string' ? rawText.trim() : '';
  if (!raw) return null;

  const src = typeof sourceInputText === 'string' ? sourceInputText.trim() : '';
  if (!src) return RAW_TEXT_STATUS.NO_ORIGINAL;

  if (src.includes(raw)) return RAW_TEXT_STATUS.VERBATIM;

  const rawNorm = normalizeForComparison(raw);
  const srcNorm = normalizeForComparison(src);
  if (rawNorm && srcNorm.includes(rawNorm)) return RAW_TEXT_STATUS.PARAPHRASED;

  return RAW_TEXT_STATUS.DIVERGENT;
};

/** true sólo cuando el texto se puede mostrar entre comillas sin mentir. */
export const isQuotable = (status) => status === RAW_TEXT_STATUS.VERBATIM;

/**
 * Chequeo de escritura: qué ítems de esta solicitud NO van a poder mostrarse
 * como cita, para poder decirlo en el momento en vez de descubrirlo al comprar.
 *
 * Se devuelve como WARNING, nunca como error: el líder está en el campo y la
 * solicitud no se bloquea (#357 paso 4). El bug es el silencio, no la paráfrasis
 * (#356).
 *
 * @param {Array<{description?: string|null, raw_text?: string|null}>} items
 * @param {string|null|undefined} sourceInputText
 * @returns {{status: string, description: string|null, raw_text: string}[]}
 */
export const findUnquotableItems = (items, sourceInputText) => {
  if (!Array.isArray(items)) return [];
  const out = [];
  for (const item of items) {
    const raw = typeof item?.raw_text === 'string' ? item.raw_text.trim() : '';
    if (!raw) continue;
    const status = classifyRawText(raw, sourceInputText);
    if (status && !isQuotable(status)) {
      out.push({ status, description: item.description ?? null, raw_text: raw });
    }
  }
  return out;
};

/**
 * El warning que se le devuelve al agente. Se separa el caso "no mandaste el
 * original" del caso "lo mandaste y no coincide" porque la acción es distinta:
 * el primero se arregla mandando el dictado completo, el segundo copiando las
 * palabras del líder en vez de las propias.
 */
export const buildRawTextWarning = (unquotable, hasOriginal) => {
  if (!unquotable.length) return null;

  if (!hasOriginal) {
    return (
      `${unquotable.length} ítem(s) guardaron un texto "literal del líder" sin que se guardara el ` +
      'dictado original (`source_input_text` vino vacío), así que NADIE puede verificar que sean sus ' +
      'palabras: la vista los muestra como "según el asistente", no entre comillas. Mandá el dictado ' +
      'COMPLETO y sin resumir en `source_input_text` para que el textual valga como registro (#369).'
    );
  }

  const sample = unquotable.slice(0, 3).map((u) => `"${u.raw_text}"`).join(', ');
  return (
    `${unquotable.length} ítem(s) tienen un texto "literal del líder" que NO aparece igual dentro del ` +
    `dictado original: ${sample}${unquotable.length > 3 ? ', ...' : ''}. Se guardaron igual, pero la ` +
    'vista NO los va a mostrar entre comillas — no reescribas el texto del líder ni resumas el ' +
    'original: copialo tal cual, con sus errores y su jerga (#357, #369).'
  );
};
